import { ContentItemFragmentFragment, EventFormat } from '@lopo-tech/lopo-api'
import { IPostPreview } from 'src/components/post/preview/post_preview'
import { IProps } from 'src/post-creation-wizard/wizards/create/steps/preview'
import DateFormatter from './date_formatter/date_formatter'
import { formatNeedsLink } from 'src/utils/event_format'
import { urlForServer } from 'src/utils/validation'

export function scrollTop () {
  // We use this in Onboarding wizard and Settings page to be sure the substep header is never hidden.
  // We have to use a negative y position to make sure to scroll above the adress bar,
  // which is not included in the viewport height
  // We also need a setTimeout to make it work on iOS
  setTimeout(() => window.scrollTo(0, -100), 0)
}

export const getPreviewContentItem = ({
  address,
  attachments,
  author,
  postType,
  teaser,
  title,
  linkPreviewParams,
  eventData
}: Pick<
  ContentItemFragmentFragment,
  'attachments' | 'author' | 'teaser' | 'title' | 'address'
> &
  Pick<IProps, 'postType'| 'eventData' | 'linkPreviewParams'>): IPostPreview => {
  const sharedProperties = {
    address,
    attachments,
    author,
    canComment: { value: false },
    canReact: { value: false },
    canSeeFullContent: { value: true },
    canDestroy: { value: false },
    canShare: { value: false },
    canUpdate: { value: false },
    canShowComments: { value: false },
    commentsCount: 0,
    id: 'CONTENT_PREVIEW',
    markers: [],
    publishedAt: new Date().toISOString(),
    reactions: {
      availableReactions: [],
      canShowLastReactorName: { value: false },
      canShowReactors: { value: false },
      id: 'REACTIONS_PREVIEW',
      receivedReactions: [],
      __typename: 'Reactions' as const,
      lastReactorName: null,
      ownReaction: null
    },
    slug: 'CONTENT_PREVIEW',
    teaser,
    title,
    linkPreviewParams,
    inclusionReason: undefined,
    hideFooter: true
  }

  switch (postType) {
    case 'event':
      return {
        __typename: 'Event',
        // start date and time should always be set at this point. this is just a safety measure and ensures types are correct
        startTime: eventData.startDate && eventData.startTime
          ? DateFormatter.toDateTimeISOString(
            eventData.startDate,
            eventData.startTime
          )
          : new Date().toISOString(),
        endTime: eventData.endDate && eventData.endTime
          ? DateFormatter.toDateTimeISOString(
            eventData.endDate,
            eventData.endTime
          )
          : undefined,
        // Both, not just the format: the location row renders the address only
        // for a format that carries one, and the link only when there is one.
        format: eventData.format ?? EventFormat.InPerson,
        onlineMeetingUrl: formatNeedsLink(eventData.format)
          ? urlForServer(eventData.onlineMeetingUrl ?? '')
          : undefined,
        ...sharedProperties
      }
    case 'snapshot':
      return {
        __typename: 'Snapshot',
        ...sharedProperties
      }
    default:
      return {
        __typename: 'Article',
        ...sharedProperties
      }
  }
}

export const shortenToTeaser = (body?: string) => {
  if (!body || body.length <= 200) {
    return body
  }
  return body.slice(0, body.lastIndexOf(' ', 200)) + '…'
}
